// CardHover — wraps any text/element; on hover fetches + displays the
// Scryfall image in a floating popover. Debounced so quick mouse-overs
// don't spam the API.

import { useEffect, useRef, useState } from "react";
import { getScryfallCard, imageFor, backImageFor, type ScryfallCard } from "../lib/scryfall";
import { cn } from "../lib/cn";
import CardImage from "./CardImage";

interface Props {
  name: string;
  children: React.ReactNode;
  className?: string;
}

const HOVER_DELAY_MS = 120;

export default function CardHover({ name, children, className }: Props) {
  const [open, setOpen] = useState(false);
  const [card, setCard] = useState<ScryfallCard | null>(null);
  const [loading, setLoading] = useState(false);
  const [pos, setPos] = useState<{ x: number; y: number; placement: "right" | "left" } | null>(null);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const anchorRef = useRef<HTMLSpanElement | null>(null);

  const schedule = (e: React.MouseEvent) => {
    const target = e.currentTarget.getBoundingClientRect();
    const rightSpace = window.innerWidth - target.right;
    // 440 covers the widest popup (two-face DFC at w-[420px] + gap). We can't
    // know DFC-ness at hover time (card loads async), so reserve for the wide
    // case to avoid a side-by-side popup clipping the right edge.
    const placement: "right" | "left" = rightSpace >= 440 ? "right" : "left";
    const x = placement === "right" ? target.right + 8 : target.left - 8;
    const y = target.top;
    setPos({ x, y, placement });

    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => {
      setOpen(true);
      if (!card && !loading) {
        setLoading(true);
        getScryfallCard(name).then((c) => {
          setCard(c);
          setLoading(false);
        });
      }
    }, HOVER_DELAY_MS);
  };

  const cancel = () => {
    if (hoverTimer.current) {
      clearTimeout(hoverTimer.current);
      hoverTimer.current = null;
    }
    setOpen(false);
  };

  useEffect(() => () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
  }, []);

  const img = card ? imageFor(card, "normal") : null;
  const backImg = card ? backImageFor(card, "normal") : null;

  return (
    <span
      ref={anchorRef}
      onMouseEnter={schedule}
      onMouseLeave={cancel}
      className={cn("cursor-default underline decoration-dotted decoration-fg-subtle underline-offset-2 hover:decoration-fg-muted", className)}
    >
      {children}
      {open && pos && (
        <div
          className="fixed z-50 anim-fade-in"
          style={{
            left: pos.placement === "right" ? pos.x : undefined,
            right: pos.placement === "left" ? window.innerWidth - pos.x : undefined,
            top: Math.max(8, Math.min(window.innerHeight - 360, pos.y)),
          }}
        >
          <div className={cn("surface p-1 shadow-card-hover", backImg ? "w-[420px]" : "w-[240px]")}>
            {loading && !card && (
              <div className="skeleton w-full aspect-[488/680]" />
            )}
            {img && (
              <CardImage front={img} back={backImg} alt={name} loading="eager" />
            )}
            {!loading && !img && (
              <div className="p-3 text-xs text-fg-muted">
                No image found for <span className="font-medium text-fg">{name}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </span>
  );
}
